import axios from 'axios';
import { useMemo, useCallback, useState, useEffect } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { RoutePath } from 'src/router';

import { useSelector } from 'src/store';
import { selectedCompanyIdSelector } from 'src/store/selectors/companySelector';

import { DangerModal } from '@itm/shared-frontend/lib/components/modals';
import { setItemToLocalStorage } from '@itm/shared-frontend/lib/utils';

import { NEW_PAYROLL_DETAILS_LS_KEY, NewPayrollDetails } from './addPayroll/constants';
import { getPayrollListSearch, PayrollListSearchParams } from 'src/api/employerHub/payroll';

import { DirectDebitStatus, ShortPayrollResponse } from 'src/types';

function DirectDebitMappingForManagers() {
  const navigate = useNavigate();
  const companyId = useSelector(selectedCompanyIdSelector);
  const [pendingPayrollCount, setPendingPayrollCount] = useState<number>(0);
  const [pendingPayroll, setPendingPayroll] = useState<ShortPayrollResponse | null>();
  const [isWarningModalActive, setIsWarningModalActive] = useState(false);

  const abortControllerSet = useMemo<Set<AbortController>>(() => new Set(), []);

  const fetchData = useCallback(async () => {
    if (!companyId) {
      return;
    }
    const abortController = new AbortController();
    abortControllerSet.add(abortController);

    try {
      const config = { signal: abortController.signal };

      const params: PayrollListSearchParams = {
        payrollStatus: 'Active',
        directDebitStatuses: [DirectDebitStatus.NotStarted, DirectDebitStatus.Failed, DirectDebitStatus.Cancelled],
      };
      const res = await getPayrollListSearch(companyId, params, config);
      const pendingPayrollCountValue = res.data.totalCount;
      setPendingPayrollCount(pendingPayrollCountValue);

      if (pendingPayrollCountValue === 1) {
        const pendingPayrollValue = res.data.items?.[0];
        setPendingPayroll(pendingPayrollValue);

        const newPayrollDetails: NewPayrollDetails = {
          // BE has not done any changes yet in direct debit details area
          schemeId:'',
          employerId: '',
          name: pendingPayrollValue?.name || '',
          payrollId: pendingPayrollValue?.id || '',
        };
        setItemToLocalStorage(NEW_PAYROLL_DETAILS_LS_KEY, newPayrollDetails);
      }

      if (pendingPayrollCountValue) {
        setIsWarningModalActive(true);
      }
    } catch (e) {
      if (axios.isCancel(e) || axios.isAxiosError(e)) return;
    } finally {
      abortControllerSet.delete(abortController); // Ensure abortController is removed
    }
  }, [companyId, abortControllerSet]);

  const isMultiplePayrolls = (pendingPayrollCount ?? 0) > 1;

  const buttonClickHandler = useCallback(() => {
    navigate(
      isMultiplePayrolls
        ? RoutePath.configurationPayrollsList
        : generatePath(RoutePath.configurationPayrollEditRoot, { payrollId: pendingPayroll?.id }),
    );

    setIsWarningModalActive(false);
  }, [isMultiplePayrolls, navigate, pendingPayroll?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const cleanup = useCallback(() => {
    abortControllerSet.forEach((abortController) => abortController.abort());
  }, [abortControllerSet]);

  useEffect(() => () => cleanup(), [cleanup]);

  return (
    <form name="directDebitForManagerForm" noValidate>
      {pendingPayrollCount > 0 && (
        <DangerModal
          headerTitle={
            isMultiplePayrolls
              ? 'Multiple Payrolls - Direct debit details required'
              : pendingPayroll?.name + ' - Direct debit details required'
          }
          isShowModal={isWarningModalActive}
          closeModal={() => setIsWarningModalActive(false)}
          Body={
            <div className="is-size-3 mb-5">
              <p className="mb-3">
                {isMultiplePayrolls
                  ? 'You are required to provide bank account details for multiple payrolls.'
                  : 'You are required to provide bank account details for payroll: ' +
                    pendingPayroll?.name +
                    '. This is to set up the direct debit mandate used for collecting the contribution money as indicated in each contribution schedule.'}
              </p>
              <p>
                You can skip this step. This will not affect the period upload and processing within the Hub. However
                you will be prompted each time you log in, until you instruct us of the details OR confirm the details
                are not required.
              </p>
            </div>
          }
          Footer={
            <>
              <button className="button is-danger" type="button" onClick={() => buttonClickHandler()}>
                Provide Bank Account Details
              </button>
              <button
                className="button is-grey is-outlined"
                type="button"
                onClick={() => setIsWarningModalActive(false)}
              >
                Skip
              </button>
            </>
          }
        />
      )}
    </form>
  );
}

export default DirectDebitMappingForManagers;
